// In-app dropdown (not the browser's native <select>): a compact button
// that opens a list in the app's own style. Use this for new dropdowns
// (operator direction, 2026-09-08). The list is portaled, so it is never
// clipped by a scrolling card or dialog; see ListboxField for keyboard.
//
//   <Dropdown ariaLabel="Age band" value={v} onChange={setV}
//     options={[{ value: "13-15", label: "13 to 15", hint: "Teen defaults" }]} />

import { ListboxField, type ListboxOption } from "./ListboxField";
import { COARSE_TOUCH, CONTROL_RADIUS, FOCUS_RING, cx } from "./controlStyles";

export type DropdownOption<T extends string> = {
  value: T;
  label: string;
  hint?: string;
  disabled?: boolean;
  /** Heading above the first option of an adjacent run (see ListboxField). */
  group?: string;
};

interface DropdownProps<T extends string> {
  value: T;
  options: DropdownOption<T>[];
  onChange: (v: T) => void;
  ariaLabel: string;
  /** id of a visible label; when set it names the field instead of ariaLabel. */
  labelledBy?: string;
  /** id of the trigger, for a <label htmlFor>. */
  id?: string;
  className?: string;
  /** Minimum width of the trigger (px). The open list is at least 220px. */
  minWidth?: number;
  /**
   * The open list takes the trigger's width and left edge, and long
   * options wrap. Default "auto": on for a trigger at least 240px wide
   * (a full-width form field), off for a compact filter, whose list is
   * content-sized and right-aligned under it.
   */
  matchTriggerWidth?: boolean | "auto";
  disabled?: boolean;
  placeholder?: string;
  /** sm = 28px, md = 32px (default). */
  size?: "sm" | "md";
}

export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  labelledBy,
  id,
  className,
  minWidth = 180,
  disabled,
  placeholder,
  size = "md",
  matchTriggerWidth = "auto",
}: DropdownProps<T>) {
  return (
    <ListboxField<T>
      value={value}
      options={options as ListboxOption<T>[]}
      onChange={onChange}
      ariaLabel={ariaLabel}
      labelledBy={labelledBy}
      id={id}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      style={{ minWidth }}
      listMinWidth={220}
      align="end"
      matchTriggerWidth={matchTriggerWidth}
      triggerClassName={cx(
        "border border-control bg-inset text-fg [@media(hover:hover)]:hover:border-fg-subtle",
        size === "sm" ? "h-[28px] px-2.5 text-xs" : "h-[32px] px-3 text-sm",
        CONTROL_RADIUS,
        FOCUS_RING,
        COARSE_TOUCH,
      )}
    />
  );
}
